<template>
  <!-- Reset password (from the emailed link), in the shared signed-out frame. -->
  <AuthShell>
    <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }">
      <h2 class="auth-title">{{ t('auth.resetTitle') }}</h2>
      <p class="auth-sub">{{ t('auth.resetSub') }}</p>

      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.newPassword') }}</span>
        <Field name="password" v-slot="{ field }">
          <input v-bind="field" type="password" name="password" autocomplete="new-password" />
        </Field>
        <span v-if="errors.password" class="auth-error">{{ errors.password }}</span>
      </label>
      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.confirmPassword') }}</span>
        <Field name="confirm_password" v-slot="{ field }">
          <input v-bind="field" type="password" name="confirm_password" autocomplete="new-password" />
        </Field>
        <span v-if="errors.confirm_password" class="auth-error">{{ errors.confirm_password }}</span>
      </label>

      <template v-if="requiresRecaptcha">
        <Field :model-value="reCaptchaV2Token" name="recaptcha" type="hidden" />
        <div class="auth-captcha" :class="{ 'is-error': Boolean(errors.recaptcha) }">
          <vue-recaptcha :sitekey="accountStore.reCaptchaSiteKey" size="normal" :hl="locale === 'zh-CN' ? 'zh-CN' : 'en'" :key="locale" theme="dark"
            :loading-timeout="recaptchaTimeout" @verify="recaptchaVerified"
            @expire="recaptchaExpired" @error="recaptchaError">
          </vue-recaptcha>
        </div>
        <span v-if="errors.recaptcha" class="auth-error" style="margin: -10px 0 12px;">{{ errors.recaptcha }}</span>
      </template>

      <button class="auth-btn" type="submit">{{ t('auth.resetButton') }}</button>
      <div class="auth-links"><router-link to="/login">{{ t('auth.backToSignIn') }}</router-link></div>
    </Form>
  </AuthShell>
</template>

<script setup>
import { t, locale } from '@/i18n';
import { Form, Field } from 'vee-validate';
import AuthShell from '../AuthShell.vue';
import * as Yup from 'yup';
import vueRecaptcha from 'vue3-recaptcha2';
import { ref, defineProps, onBeforeMount } from "vue";
import { useAccountStore, useAlertStore } from '@/stores';
import { useRouter } from 'vue-router';
const router = useRouter();
const alertStore = useAlertStore();
const accountStore = useAccountStore();
const props = defineProps({
    resetid: {
        type: String, // Assuming user object has a name property
    },
    email: {
        type: String, // Assuming user object has a name property
    },
});
onBeforeMount( async () => {
  const isSuccess = await accountStore.verifyid({email: props.email, resetid: props.resetid})
  if(!isSuccess) {
    router.push({path: '/forgotpass'});
  }
})
// Keep password recovery available while the production reCAPTCHA key is invalid.
// Registration retains its separate reCAPTCHA requirement.
const requiresRecaptcha = false
const schema = Yup.object().shape({
  password: Yup.string().required(() => t('auth.v.passwordRequired')).min(8, () => t('auth.v.passwordMin')),
  confirm_password: Yup.string().required(() => t('auth.v.confirmRequired')).oneOf([Yup.ref('password')], () => t('auth.v.passwordsMatch')),
  ...(requiresRecaptcha ? { recaptcha: Yup.string().required(() => t('auth.v.captcha')) } : {}),
});

const recaptchaTimeout = ref(30000);
const reCaptchaV2Token = ref('');

function recaptchaVerified(response) {
  reCaptchaV2Token.value = response;
}

function recaptchaExpired() {
  reCaptchaV2Token.value = '';
}

function recaptchaError(reason) {
  reCaptchaV2Token.value = '';
  alertStore.error(t('auth.recaptchaError'));
}

async function onSubmit(values) {
  try {
    await accountStore.resetpassword({email: props.email, password: values.password, resetid: props.resetid});
  } catch (error) {
    console.error(error)
  }
}

</script>
